/**
 * usePrompts Hook
 *
 * React Query hooks for managing prompt version state.
 * Part of the GO-Lab Prompt Management feature.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { promptService } from '../services/promptService';
import type {
  PromptKeySummary,
  PromptVersionSummary,
  PromptVersionDetail,
  PromptDiffResponse,
  PromptRenderResponse,
  CreatePromptDraftOptions,
  UpdatePromptDraftOptions,
  TemplateVariablesResponse,
} from '../types/prompt';

// =============================================================================
// Query Keys
// =============================================================================

export const promptKeys = {
  all: ['prompts'] as const,
  keys: (taxonomyKey: string | null) => [...promptKeys.all, 'keys', taxonomyKey] as const,
  live: (taxonomyKey: string) => [...promptKeys.all, 'live', taxonomyKey] as const,
  versions: (promptKey: string, taxonomyKey: string) => [...promptKeys.all, 'versions', taxonomyKey, promptKey] as const,
  versionDetail: (id: string) => [...promptKeys.all, 'detail', id] as const,
  diff: (a: string, b: string) => [...promptKeys.all, 'diff', a, b] as const,
  render: (id: string, taxId?: string) => [...promptKeys.all, 'render', id, taxId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export const usePromptKeys = (taxonomyKey?: string | null) =>
  useQuery<PromptKeySummary[]>({
    queryKey: promptKeys.keys(taxonomyKey ?? 'all'),
    queryFn: () => promptService.listPromptKeys(taxonomyKey),
    staleTime: 2 * 60 * 1000,
  });

export const useLivePrompts = (taxonomyKey: string) =>
  useQuery<Record<string, PromptVersionSummary>>({
    queryKey: promptKeys.live(taxonomyKey),
    queryFn: () => promptService.getLivePrompts(taxonomyKey),
    enabled: !!taxonomyKey,
    staleTime: 2 * 60 * 1000,
  });

export const usePromptVersions = (promptKey: string, taxonomyKey: string) =>
  useQuery<PromptVersionSummary[]>({
    queryKey: promptKeys.versions(promptKey, taxonomyKey),
    queryFn: () => promptService.listVersions(promptKey, { taxonomyKey }),
    enabled: !!promptKey && !!taxonomyKey,
  });

export const usePromptDetail = (versionId: string) =>
  useQuery<PromptVersionDetail>({
    queryKey: promptKeys.versionDetail(versionId),
    queryFn: () => promptService.getVersion(versionId),
    enabled: !!versionId,
  });

export const useTemplateVariables = () =>
  useQuery<TemplateVariablesResponse>({
    queryKey: [...promptKeys.all, 'template-variables'],
    queryFn: promptService.getTemplateVariables,
    staleTime: Infinity,
  });

export const usePromptDiff = (versionAId: string, versionBId: string, enabled = true) =>
  useQuery<PromptDiffResponse>({
    queryKey: promptKeys.diff(versionAId, versionBId),
    queryFn: () => promptService.getDiff(versionAId, versionBId),
    enabled: enabled && !!versionAId && !!versionBId,
    staleTime: 0,
    gcTime: 0,
  });

export const useRenderedPrompt = (versionId: string, taxonomyVersionId?: string) =>
  useQuery<PromptRenderResponse>({
    queryKey: promptKeys.render(versionId, taxonomyVersionId),
    queryFn: () => promptService.renderPrompt(versionId, taxonomyVersionId),
    enabled: !!versionId,
    staleTime: 0,
  });

export const usePromptPreview = (
  templateContent: string,
  taxonomyKey: string,
  dimensionKey?: string | null,
) =>
  useQuery<PromptRenderResponse, Error>({
    queryKey: [
      ...promptKeys.all,
      'preview',
      taxonomyKey,
      dimensionKey ?? null,
      templateContent,
    ],
    queryFn: () =>
      promptService.previewPrompt(templateContent, taxonomyKey, dimensionKey),
    enabled: !!templateContent && !!taxonomyKey,
    staleTime: 0,
    // Drop stale debounced previews fast — the query key embeds the full
    // document, so an editing session would otherwise pile up many KB
    // of dead cache entries until React Query's default 5-min gcTime.
    gcTime: 30_000,
    retry: false,
  });

// =============================================================================
// Mutation Hooks
// =============================================================================

export const useCreatePromptDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PromptVersionSummary,
    Error,
    { promptKey: string; options?: CreatePromptDraftOptions }
  >({
    mutationFn: ({ promptKey, options }) =>
      promptService.createDraft(promptKey, options),
    onSuccess: () => {
      toast.success('Draft created');
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create draft');
    },
  });
};

export const useUpdatePromptDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PromptVersionSummary,
    Error,
    { versionId: string; data: UpdatePromptDraftOptions }
  >({
    mutationFn: ({ versionId, data }) =>
      promptService.updateDraft(versionId, data),
    onSuccess: (_, { versionId }) => {
      toast.success('Draft saved');
      // Invalidate everything under the `prompts` namespace — versions
      // list (for updated linesAdded/linesRemoved diff stats), version
      // detail, and any diff queries. Matches what create/deploy/rollback
      // do so the detail view always reflects the saved content.
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
      queryClient.invalidateQueries({ queryKey: promptKeys.versionDetail(versionId) });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save draft');
    },
  });
};

export const useDeployPrompt = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PromptVersionSummary,
    Error,
    { versionId: string; deploymentNote: string }
  >({
    mutationFn: ({ versionId, deploymentNote }) =>
      promptService.deployVersion(versionId, deploymentNote),
    onSuccess: () => {
      toast.success('Prompt deployed to live');
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to deploy prompt');
    },
  });
};

export const useRollbackPrompt = () => {
  const queryClient = useQueryClient();
  return useMutation<
    PromptVersionSummary,
    Error,
    { versionId: string; note: string }
  >({
    mutationFn: ({ versionId, note }) =>
      promptService.rollbackToVersion(versionId, note),
    onSuccess: () => {
      toast.success('Prompt rolled back');
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to rollback prompt');
    },
  });
};

export const useDeletePromptDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationFn: (versionId) => promptService.deleteDraft(versionId),
    onSuccess: () => {
      toast.success('Draft deleted');
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete draft');
    },
  });
};
